import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { MapPin, Ruler, Check, CalendarIcon, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import { availableDurations, formatPrice, DURATION_MAP } from "@/lib/durations";
import { resolveImageUrl } from "@/components/ImageUploader";

export default function DockDetails() {
  const { id } = useParams();
  const [dock, setDock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [durationUnit, setDurationUnit] = useState(null);
  const [qty, setQty] = useState(1);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/docks/${id}`).then((r) => {
      setDock(r.data);
      const durs = availableDurations(r.data);
      if (durs.length) setDurationUnit(durs[0].unit);
    }).finally(() => setLoading(false));
  }, [id]);

  const durs = useMemo(() => (dock ? availableDurations(dock) : []), [dock]);
  const activeDur = durs.find((d) => d.unit === durationUnit);

  const submit = async () => {
    if (!user) { navigate("/login"); return; }
    if (!startDate) { toast.error("Pick a start date"); return; }
    if (!activeDur) { toast.error("Choose a duration"); return; }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        dock_id: dock.dock_id,
        start_date: startDate.toISOString().slice(0, 10),
        duration_unit: activeDur.unit,
        duration_quantity: Number(qty),
        message,
      });
      toast.success("Booking request sent to the owner!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const messageOwner = async () => {
    if (!user) { navigate("/login"); return; }
    if (!dock?.owner?.user_id) { toast.error("Owner info unavailable"); return; }
    try {
      const { data } = await api.post("/conversations", {
        other_user_id: dock.owner.user_id,
        dock_id: dock.dock_id,
      });
      navigate("/messages", { state: { conversationId: data.conversation_id } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="container-editorial py-16">Loading…</div>;
  if (!dock) return <div className="container-editorial py-16">Dock not found</div>;

  const subtotal = activeDur ? activeDur.price * Number(qty || 0) : 0;
  const creditsAvail = user?.credit_balance || 0;
  const creditsApplied = Math.min(creditsAvail, subtotal);
  const youPay = Math.max(0, subtotal - creditsApplied);

  const qtyLabel = activeDur ? activeDur.labelPlural : "units";

  return (
    <div data-testid="dock-details-page" className="container-editorial py-10">
      <div className="mb-2 text-xs uppercase tracking-[0.3em] text-accent">{dock.lake}</div>
      <h1 className="font-display text-4xl sm:text-5xl mb-3">{dock.title}</h1>
      <div className="flex items-center gap-5 text-sm text-muted-foreground mb-8">
        <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" strokeWidth={1.5}/>{dock.city}, {dock.zip_code}</span>
        <span className="inline-flex items-center gap-1"><Ruler className="h-3.5 w-3.5" strokeWidth={1.5}/>Up to {dock.max_boat_length_ft} ft</span>
        {dock.accommodates_pwc && (
          <span data-testid="pwc-supported-tag" className="inline-flex items-center gap-1 text-accent font-medium">
            🌊 Jet skis welcome
          </span>
        )}
      </div>

      <div className="grid grid-cols-4 grid-rows-2 gap-3 rounded-2xl overflow-hidden mb-12 h-[420px]">
        <img src={resolveImageUrl(dock.images[0])} alt="" className="col-span-2 row-span-2 h-full w-full object-cover"/>
        {(dock.images.slice(1, 5).length ? dock.images.slice(1, 5) : [dock.images[0], dock.images[0], dock.images[0], dock.images[0]]).map((im, i) => (
          <img key={`${im}-${i}`} src={resolveImageUrl(im)} alt="" className="h-full w-full object-cover"/>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 pb-6 border-b border-border">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
              {(dock.owner?.name || "?").charAt(0)}
            </div>
            <div>
              <div className="font-medium">Hosted by {dock.owner?.name || "the owner"}</div>
              <div className="text-xs text-muted-foreground">Verified owner · Responds within a day</div>
            </div>
          </div>
          <div className="py-8 border-b border-border">
            <h2 className="font-display text-2xl mb-4">About this dock</h2>
            <p className="text-foreground/85 leading-relaxed">{dock.description}</p>
          </div>
          <div className="py-8 border-b border-border">
            <h2 className="font-display text-2xl mb-4">Rental options</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {durs.map((d) => (
                <div key={d.unit} data-testid={`dock-price-${d.unit}`} className="rounded-xl border border-border bg-card p-4 flex items-baseline justify-between">
                  <span className="text-sm capitalize">{d.labelSingular}</span>
                  <span><span className="font-medium">{formatPrice(d.price)}</span><span className="text-muted-foreground text-sm">{d.shortSuffix}</span></span>
                </div>
              ))}
            </div>
          </div>
          <div className="py-8">
            <h2 className="font-display text-2xl mb-4">Amenities</h2>
            <ul className="grid grid-cols-2 gap-3">
              {dock.amenities.map((a) => (
                <li key={a} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-accent" strokeWidth={2}/>{a}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div data-testid="booking-widget" className="sticky top-24 rounded-2xl border border-border bg-card p-6 shadow-sm">
            {activeDur ? (
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-display text-3xl">{formatPrice(activeDur.price)}</span>
                <span className="text-muted-foreground">{activeDur.shortSuffix}</span>
              </div>
            ) : (
              <div className="mb-6 text-sm text-muted-foreground">No rental options available.</div>
            )}

            <label className="text-xs uppercase tracking-widest text-muted-foreground">Rent by</label>
            <div className="grid grid-cols-2 gap-2 mt-1 mb-4">
              {durs.map((d) => (
                <button
                  key={d.unit}
                  type="button"
                  data-testid={`duration-tab-${d.unit}`}
                  onClick={() => setDurationUnit(d.unit)}
                  className={`text-sm py-2 px-3 rounded-lg border capitalize transition-colors ${durationUnit === d.unit ? "border-primary bg-primary/5 text-primary font-medium" : "border-border hover:border-primary/40"}`}
                >
                  {d.labelSingular}
                </button>
              ))}
            </div>

            <label className="text-xs uppercase tracking-widest text-muted-foreground">Start date</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button data-testid="booking-date-btn" variant="outline" className="w-full justify-start mt-1 mb-4 font-normal">
                  <CalendarIcon className="h-4 w-4 mr-2" strokeWidth={1.5}/>
                  {startDate ? format(startDate, "PP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={startDate} onSelect={setStartDate} disabled={(d) => d < new Date()} initialFocus />
              </PopoverContent>
            </Popover>

            <label className="text-xs uppercase tracking-widest text-muted-foreground">Number of {qtyLabel}</label>
            <Input data-testid="booking-qty-input" type="number" min={1} max={365} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 mb-4"/>

            <label className="text-xs uppercase tracking-widest text-muted-foreground">Message to owner (optional)</label>
            <Textarea data-testid="booking-message-input" value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1 mb-4" placeholder="Tell the owner about your boat…" rows={3}/>

            {activeDur && (
              <div className="border-t border-border pt-4 mb-4 space-y-1 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>{formatPrice(activeDur.price)} × {qty} {qtyLabel}</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                {creditsApplied > 0 && (
                  <div className="flex justify-between text-accent" data-testid="credit-preview">
                    <span>Credit applied</span>
                    <span>−${creditsApplied.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium text-base pt-2 border-t border-border/60">
                  <span>You pay</span>
                  <span data-testid="booking-total">${youPay.toFixed(2)}</span>
                </div>
              </div>
            )}

            <Button data-testid="request-booking-btn" onClick={submit} disabled={submitting || !activeDur} className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">
              {submitting ? "Sending…" : "Request booking"}
            </Button>
            {user && dock.owner?.user_id && dock.owner.user_id !== user.user_id && (
              <Button
                data-testid="message-owner-btn"
                onClick={messageOwner}
                variant="outline"
                className="w-full rounded-full mt-2"
              >
                <MessageSquare className="h-4 w-4 mr-2" strokeWidth={1.75}/>
                Message the owner
              </Button>
            )}
            <p className="mt-3 text-xs text-muted-foreground text-center">You won't be charged until the owner approves.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
